import React from 'react'

import BasicLgyInputField from 'app/components/util/lgy-input-field/BasicLgyInputField'
import BasicLgySelectField from 'app/components/util/lgy-select-field/BasicLgySelectField'
import BasicLgyCheckbox from 'app/components/util/lgy-checkbox/BasicLgyCheckbox'
import LgyFormSubmit from 'app/components/util/lgy-form-submit/LgyFormSubmit'
import LgyAlertDialog from 'app/components/util/lgy-alert-dialog/LgyAlertDialog'
import { namedMinLength, namedValueBetween } from 'app/utils/validations'

const AuditSearchForm = ({ auditUnit, fiscalYear, auditType, auditNumber, organizationId, organizationName, auditLocation, activeIndicator, auditSearch,
  handleSubmit, handleChange, handleReset, user, canSearchAll, handleBlur, fullAuditNumber, handleRetrieveAudit }) => {
  const affiliations = user.id ? user.affiliations : []
  const externalOversightUser = user.id && user.authorizations.some(auth => auth === 'ROLE_External Oversight User')
  const validateFullAuditNumber = value => value && !/((([^Ll](T|t)?)|((s|S)?(v|V)?)){1})([0-9][0-9][0-9][0-9])-([0-9][0-9][0-9])/.test(value) ? 'Full Audit Number must be in the correct format' : undefined

  return <div className='audit-search-form usa-grid'>
    <h1>Audit Search</h1>
    {auditSearch.list && auditSearch.list.length < 1 &&
      <LgyAlertDialog type='error' heading='Not Found' text='No audits were found that match the search criteria.' />}
    {auditUnit === 'M' && <form onSubmit={handleRetrieveAudit} autoComplete='off'>
      <fieldset>
        <legend>Enter the full audit number if you know it.</legend>
        <BasicLgyInputField label='Full Audit Number' type='text' id='fullAuditNumber' name='fullAuditNumber' maxLength='12'
          value={fullAuditNumber} onChange={handleChange} onBlur={handleBlur} error={validateFullAuditNumber(fullAuditNumber)}
          tooltip='Audit number is in the format of <Audit Location><Fiscal Year>-<Order of Creation>, e.g., a virtual audit performed on a lender 
          that was the 3rd audit of the fiscal year 2018 would be V2018-003. An "S" should be added to the beginning for servicer audits: 
          SV2018-003 or S2018-003 for travel audits.' autoFocus={!canSearchAll && auditUnit === 'M'} />
        <LgyFormSubmit
          pristine={fullAuditNumber === ''}
          submitting={auditSearch.fetching}
          submitFailed={auditSearch.error}
          reset={handleReset} />
      </fieldset>
    </form>}
    <form onSubmit={handleSubmit} autoComplete='off'>
      {canSearchAll && <fieldset>
        <legend>Select the auditing unit.</legend>
        <ul className='usa-unstyled-list'>
          <li>
            <BasicLgyCheckbox id='mu' type='radio' name='auditUnit' label='Monitoring Unit' value='M' checked={auditUnit === 'M'}
              onChange={handleChange} autoFocus />
          </li>
          <li>
            <BasicLgyCheckbox id='qa' type='radio' name='auditUnit' label='Quality Assurance' value='Q' checked={auditUnit === 'Q'}
              onChange={handleChange} />
          </li>
        </ul>
      </fieldset>}
      <fieldset>
        <legend>{`Enter the ${auditUnit === 'M' ? 'audit number, ' : ''} fiscal year${auditUnit === 'M' ? ',' : ''} and select the audit type.`}</legend>
        {auditUnit === 'M' && <BasicLgyInputField label='3-Digit Audit Number' type='number' id='auditNumber' name='auditNumber' min='1' max='999' placeholder='3-Digit Audit Number'
          value={auditNumber} onChange={handleChange} autoFocus={!canSearchAll && auditUnit === 'M'} error={namedMinLength('Audit Number', '3')(auditNumber)} />}
        <BasicLgyInputField label='Fiscal Year' type='number' id='fiscalYear' name='fiscalYear' min='1900' max='2100' placeholder='YYYY'
          value={fiscalYear} onChange={handleChange} autoFocus={!canSearchAll && auditUnit !== 'M'} error={namedValueBetween('Fiscal Year', 1900, 2100)(fiscalYear)} />
        <BasicLgySelectField label='Audit Type' id='auditType' name='auditType' value={auditType} onChange={handleChange}>
          {!(affiliations.some(aff => aff.name === 'SERVICER') && externalOversightUser) && <option value='L'>Loan Origination</option>}
          {!(affiliations.some(aff => aff.name === 'LENDER') && externalOversightUser) && <option value='S'>Servicing</option>}
          {auditUnit === 'Q' && <option value='O'>OIG Inquiry</option>}
          {auditUnit === 'Q' && <option value='R'>Other</option>}
        </BasicLgySelectField>
      </fieldset>
      {!affiliations.some(aff => aff.name === 'LENDER' || aff.name === 'SERVICER') && <fieldset>
        <legend>Enter the Lender/Servicer ID and name.</legend>
        <BasicLgyInputField label='Lender/Servicer ID' type='number' id='organizationId' name='organizationId' min='0' max='999999' value={organizationId}
          onChange={handleChange} tooltip='Please enter the first 6 digits of the Lender/Servicer ID.' error={namedMinLength('Lender/Servicer ID', '6')(organizationId)} />
        <BasicLgyInputField label='Lender/Servicer Name' type='text' id='organizationName' name='organizationName' maxLength='100'
          value={organizationName} onChange={handleChange} />
      </fieldset>}
      <fieldset>
        <legend>Select the audit location.</legend>
        <ul className='usa-unstyled-list'>
          <li>
            <BasicLgyCheckbox id='travel' type='radio' name='auditLocation' label='Travel' value='T' checked={auditLocation === 'T'}
              onChange={handleChange} />
          </li>
          <li>
            <BasicLgyCheckbox id='virtual' type='radio' name='auditLocation' label='Virtual' value='V' checked={auditLocation === 'V'}
              onChange={handleChange} />
          </li>
        </ul>
      </fieldset>
      <fieldset>
        <legend>Select active or inactive.</legend>
        <ul className='usa-unstyled-list'>
          <li>
            <BasicLgyCheckbox id='active' type='radio' name='activeIndicator' label='Active' value='active' checked={activeIndicator === 'active'}
              onChange={handleChange} />
          </li>
          <li>
            <BasicLgyCheckbox id='inactive' type='radio' name='activeIndicator' label='Inactive' value='inactive' checked={activeIndicator === 'inactive'}
              onChange={handleChange} />
          </li>
        </ul>
      </fieldset>
      <LgyFormSubmit
        pristine={auditUnit === '' && fiscalYear === '' && auditType === '' && auditNumber === '' && organizationId === ''
          && organizationName === '' && auditLocation === '' && activeIndicator === ''}
        submitting={auditSearch.fetching}
        submitFailed={auditSearch.error}
        reset={handleReset} />
    </form>
  </div>
}

export default AuditSearchForm
